import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useSubjectMember } from '@healthblocks-io/core'
import { t } from '@lingui/macro'
import { useTheme } from 'styled-components'

import { Flex, FlexColumn } from '@/@bothrs-ui/web/components/layout'
import { Line } from '@/@bothrs-ui/web/components/primitives/line/Line'
import { Span } from '@/@bothrs-ui/web/components/typography/Body'
import { H4 } from '@/@bothrs-ui/web/components/typography/Heading'
import { mixpanel } from '@/mixpanel'
import { useLogout } from '@/modules/auth/hooks/useLogout'
import { useServer } from '@/modules/healthblocks/ServerProvider'
import { formatLocale } from '@/modules/shared/helpers/locale'
import { useSwitchHospital } from '@/modules/shared/hooks/useSwitchHospital'
import { StCurrentUserCardContainer, StLogoutButton, StSwapPersonButton } from './CurrentUserCard.styled'

type CurrentUserCardProps = {
  showSwitchTenant: boolean
}

export const CurrentUserCard = ({ showSwitchTenant }: CurrentUserCardProps) => {
  const theme = useTheme()

  const subjectMember = useSubjectMember()
  const server = useServer()

  const switchHospital = useSwitchHospital()
  const { softLogout, logout } = useLogout()

  const fullName = `${subjectMember?.givenName} ${subjectMember?.familyName}`
  return (
    <StCurrentUserCardContainer>
      <Flex gap={theme.UI.Spacing.Space4} alignItems='center'>
        <img style={{ width: '60%', maxWidth: '60px' }} src={'/img/svg/empty-avatar.svg'} alt="gebruiker's avatar" />
        <FlexColumn justifyItems='center'>
          <H4 style={{ marginBottom: theme.UI.Spacing.Space1 }}>{fullName}</H4>
          {subjectMember?.birthDate ? (
            <Span type='BodyMediumRegular'>{formatLocale(subjectMember?.birthDate, 'dd/MM/yy')}</Span>
          ) : null}
        </FlexColumn>
      </Flex>
      <Line />
      {showSwitchTenant ? (
        <>
          <a
            href='/select-hospital'
            style={{ cursor: 'pointer' }}
            onClick={(event_) => {
              event_.preventDefault()
              switchHospital()
            }}
          >
            <FlexColumn gap={theme.UI.Spacing.Space3}>
              <Flex justifyContent='space-between' alignItems='center'>
                <Flex gap={theme.UI.Spacing.Space3} alignItems='center'>
                  <FontAwesomeIcon icon={['fal', 'arrows-repeat']} />
                  <Span type='BodyMediumRegular'>
                    {t({
                      message: 'Wissel van ziekenhuis',
                      id: 'profile.switch-tenant.title',
                    })}
                  </Span>
                </Flex>

                <FontAwesomeIcon icon={['far', 'chevron-right']} />
              </Flex>
              <Flex justifyContent='space-between' alignItems='center'>
                <Span type='BodySmallRegular' style={{ maxWidth: '70%' }}>
                  {t({
                    message: 'Ingelogd bij {tenant}.',
                    id: 'profile.switch-tenant.description',
                    values: { tenant: server?.display },
                  })}
                </Span>
                {server?.logo && (
                  <img
                    src={server.logo}
                    alt={server.display}
                    style={{
                      maxHeight: '24px',
                      maxWidth: '80px',
                      objectFit: 'contain',
                    }}
                  />
                )}
              </Flex>
            </FlexColumn>
          </a>
          <Line />
        </>
      ) : null}
      <StSwapPersonButton
        onClick={() => {
          mixpanel.track('account_switch_profile')
          softLogout()
        }}
      >
        <FlexColumn gap={theme.UI.Spacing.Space3}>
          <Flex justifyContent='space-between' alignItems='center'>
            <Flex gap={theme.UI.Spacing.Space3} alignItems='center'>
              <FontAwesomeIcon icon={['fal', 'arrows-repeat']} />
              <Span type='BodyMediumRegular'>
                {t({
                  message: 'Wissel van persoon',
                  id: 'profile.sign.out.swap.title',
                })}
              </Span>
            </Flex>

            <FontAwesomeIcon icon={['far', 'chevron-right']} />
          </Flex>
          <Span type='BodySmallRegular'>
            {t({
              message: 'Beheer het profiel van iemand anders. Meld je aan met eHealth.',
              id: 'profile.sign.out.swap.description',
            })}
          </Span>
        </FlexColumn>
      </StSwapPersonButton>
      <Line />
      <StLogoutButton
        onClick={() => {
          mixpanel.track('account_logout')
          logout()
        }}
      >
        <Flex justifyContent='space-between' alignItems='center'>
          <Flex gap={theme.UI.Spacing.Space3} alignItems='center'>
            <FontAwesomeIcon icon={['fal', 'arrow-right-from-bracket']} />
            <Span type='BodyMediumRegular'>{t({ message: 'Afmelden', id: 'profile.sign.out.card' })}</Span>
          </Flex>
          <FontAwesomeIcon icon={['far', 'chevron-right']} />
        </Flex>
      </StLogoutButton>
    </StCurrentUserCardContainer>
  )
}
